import * as React from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import { useIsDesktop } from '../../hooks/useIsDesktop';

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function IconChevronDown(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export type SectionAccordionProps = {
  title: React.ReactNode;
  summary?: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  grid?: boolean;
  children: React.ReactNode;
};

export default function SectionAccordion({
  title,
  summary,
  defaultOpen,
  className = '',
  grid = true,
  children,
}: SectionAccordionProps) {
  const isDesktop = useIsDesktop();

  // Auto-expand on desktop if defaultOpen is not explicitly set
  const shouldBeOpen = defaultOpen !== undefined ? defaultOpen : isDesktop;
  return (
    <section className={cx('rounded-2xl border border-slate-200 dark:border-slate-800 bg-card text-card-foreground shadow-sm', className)}>
      <Disclosure defaultOpen={shouldBeOpen}>
        {({ open }: { open: boolean }) => (
          <div>
            <Disclosure.Button
              className={cx(
                'w-full flex items-center justify-between gap-3 px-4 py-3 lg:px-5 lg:py-4',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-2xl'
              )}
            >
              <div className="text-left">
                <h2 className="text-lg font-semibold leading-tight">{title}</h2>
                {summary && !open && (
                  <div className="mt-1 text-sm text-muted-foreground">{summary}</div>
                )}
              </div>
              <IconChevronDown className={cx('h-6 w-6 transition-transform duration-200', open && 'rotate-180')} />
            </Disclosure.Button>

            <Transition
              enter="transition duration-200 ease-out"
              enterFrom="transform scale-y-95 opacity-0"
              enterTo="transform scale-y-100 opacity-100"
              leave="transition duration-150 ease-in"
              leaveFrom="transform scale-y-100 opacity-100"
              leaveTo="transform scale-y-95 opacity-0"
            >
              <Disclosure.Panel static>
                <div className="px-4 pb-4 lg:px-5 lg:pb-5">
                  <div className={cx(grid ? 'flex flex-col gap-3 lg:grid lg:grid-cols-1 lg:gap-6' : '')}>{children}</div>
                </div>
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    </section>
  );
}


